import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Box,
  IconButton,
  TextField,
  Typography,
  Avatar,
  Chip
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import CloseIcon from "@mui/icons-material/Close";

interface Message {
  id: number;
  text: string;
  sender: "AI" | "User";
  timestamp: string;
  attachments?: File[];
}

interface ChatBoxComponentProps {
  onClose: () => void;
}

const ChatBoxComponent: React.FC<ChatBoxComponentProps> = ({ onClose }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState("");
  const messageEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = useCallback(() => {
    if (messageEndRef.current) {
      messageEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const handleSendMessage = useCallback(() => {
    if (!inputValue.trim()) return;
    const newMessage: Message = {
      id: messages.length + 1,
      text: inputValue,
      sender: "User",
      timestamp: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
    };
    setMessages((prev) => [...prev, newMessage]);
    setInputValue("");
  }, [inputValue, messages.length]);

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 2,
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        <Typography fontWeight={600}>AI Assistant</Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, p: 2, overflowY: "auto" }}>
        {messages.map((msg) => (
          <Box
            key={msg.id}
            sx={{
              display: "flex",
              justifyContent: msg.sender === "User" ? "flex-end" : "flex-start",
              mb: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                gap: 1,
                alignItems: "flex-start",
                maxWidth: "70%",
              }}
            >
              {msg.sender === "AI" && (
                <Avatar sx={{ bgcolor: "#303f9f", fontSize: 14 }}>
                  <AutoFixHighIcon fontSize="small" />
                </Avatar>
              )}
              <Box
                sx={{
                  bgcolor: msg.sender === "User" ? "#e3f2fd" : "#f5f5f5",
                  p: 1.5,
                  borderRadius: 2,
                }}
              >
                {msg.attachments?.map((file, i) => (
                  <Chip key={i} label={file.name} sx={{ mb: 0.5 }} />
                ))}
                <Typography variant="body2">{msg.text}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {msg.timestamp}
                </Typography>
              </Box>
            </Box>
          </Box>
        ))}
        <div ref={messageEndRef} />
      </Box>
      <Box
        sx={{
          p: 2,
          borderTop: "1px solid #e0e0e0",
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <TextField
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyPress={(e) => e.key === "Enter" && handleSendMessage()}
          placeholder="Type a message..."
          variant="outlined"
          size="small"
          fullWidth
        />
        <IconButton color="primary" onClick={handleSendMessage}>
          <SendIcon />
        </IconButton>
      </Box>
    </Box>
  );
};

export default ChatBoxComponent;